import { z } from "zod";

/**
 * Every EU region a tenant cluster could be provisioned into, declared ONCE
 * with the two independent facts that decide whether it is actually selectable.
 *
 * **Membership of this map is the EU-residency decision (RGPD).** A tenant's
 * whole data plane — Aurora, S3, KMS, Nomad — stays in its region, so a non-EU
 * region is rejected at the API boundary before the use case runs. London
 * (`eu-west-2`) is absent on purpose: post-Brexit the UK is outside the EU, so
 * it fails EU-residency even though AWS labels it `eu-*`. Add a region here
 * only after confirming it is an EU-member region, or one under a standing
 * adequacy decision the legal team has signed off on.
 *
 * **`enabledInAccount` is the reachability decision.** `eu-south-1` and
 * `eu-south-2` are AWS OPT-IN regions and are not enabled on account
 * 906247013001, so nothing can be created or copied there at all. The flag
 * records what the account CAN do; it does not ask AWS for anything.
 *
 * ## Why both facts live in one declaration
 *
 * The selectable set and the reachable set have to be the same set: a picker
 * that offers a region the account has not opted into provisions a tenant that
 * hard-fails at provision time. So both come from one declaration.
 *
 * ## Enabling a region is a one-word edit, and the gates find the rest
 *
 * Do the AWS opt-in first, then flip `enabledInAccount` to `true`. Everything
 * that must follow then fails until it does:
 *
 *   - `VIBE_ALLOWED_REGIONS` below — `allowed-regions.test.ts` holds it equal to
 *     the enabled regions, so an un-added region fails there.
 *   - the DR destination map in
 *     `apps/backend/src/vibe/domain/services/dr-region.ts` — a
 *     `Record<VibeAllowedRegion, string>`, so a missing row is a COMPILE error.
 *   - the provisioner's tenant regions —
 *     `apps/vibe-provisioner/stacks/_shared/pins.test.ts` reads this map from
 *     source and pins each region to the correct side of `image/pins.json`
 *     (`tenant_regions` or `regions_not_opted_in`).
 *
 * Retiring a region is the same edit in reverse, through the same gates.
 */
export const VIBE_EU_REGIONS = {
  "eu-west-1": { label: "Ireland", enabledInAccount: true },
  "eu-west-3": { label: "Paris", enabledInAccount: true },
  "eu-central-1": { label: "Frankfurt", enabledInAccount: true },
  "eu-north-1": { label: "Stockholm", enabledInAccount: true },
  "eu-south-1": { label: "Milan", enabledInAccount: false },
  "eu-south-2": { label: "Spain", enabledInAccount: false }
} as const;

/** Every EU region declared above, whether or not the account can reach it. */
export type VibeEuRegion = keyof typeof VIBE_EU_REGIONS;

/**
 * A region a tenant cluster may actually be provisioned into: an EU region the
 * account can reach.
 *
 * Derived from `VIBE_EU_REGIONS` rather than written out, so this type cannot
 * name a region the account cannot reach. Every downstream contract narrows
 * with it — the Zod enum below, the API request bodies built on it, and the
 * exhaustive `Record<VibeAllowedRegion, …>` in the DR destination map.
 */
export type VibeAllowedRegion = {
  [K in VibeEuRegion]: (typeof VIBE_EU_REGIONS)[K]["enabledInAccount"] extends true ? K : never;
}[VibeEuRegion];

/**
 * The regions every provisioning surface offers, in display order.
 *
 * `satisfies readonly VibeAllowedRegion[]` is the enforcement: listing a region
 * the account cannot reach is a COMPILE error here, which is exactly the defect
 * this list used to carry. It stays spelled out rather than computed for two
 * reasons — the order is a product decision no derivation can supply, and
 * `z.enum` needs a literal tuple type, which `Array.prototype.filter` cannot
 * produce without a cast that would defeat the check above.
 *
 * `satisfies` only constrains what may be ADDED. `allowed-regions.test.ts`
 * closes the other direction, holding this list equal to the enabled regions so
 * a newly-enabled region cannot be silently left out of the picker.
 */
export const VIBE_ALLOWED_REGIONS = [
  "eu-west-1",
  "eu-west-3",
  "eu-central-1",
  "eu-north-1"
] as const satisfies readonly VibeAllowedRegion[];

export const VibeAllowedRegionSchema = z.enum(VIBE_ALLOWED_REGIONS);
